import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { parseVariableTokens, serialiseVariableToken, type IVariableToken } from "../variable-token";
import {
    TEMPLATE_BODY_REGEX,
    TEMPLATE_COMMAND_PREFIX,
    TEMPLATE_TOKEN_KIND,
    TEMPLATE_TOKEN_TITLE,
    TEMPLATE_TOKEN_VALUE_KEY,
} from "./template-mention.constants";
import type { ITemplateMentionAttributes } from "./template-mention.types";

/**
 * The variable token a template stands for: `{{template:(aiChatReportTemplateId=<id>)|Template|<command>}}`.
 * @param template The template's id and command.
 * @returns The token in its parsed form.
 */
export const templateToVariableToken = ({
    aiChatReportTemplateId,
    command,
}: ITemplateMentionAttributes): IVariableToken => ({
    type: TEMPLATE_TOKEN_KIND,
    values: { [TEMPLATE_TOKEN_VALUE_KEY]: [aiChatReportTemplateId] },
    title: TEMPLATE_TOKEN_TITLE,
    labels: [command],
});

/**
 * The template a parsed variable token stands for, or null when it is not a template token with exactly one id.
 * @param variable A parsed variable token.
 * @returns The template's id and command, or null.
 */
export const templateFromVariableToken = (variable: IVariableToken): ITemplateMentionAttributes | null => {
    const ids = variable.values[TEMPLATE_TOKEN_VALUE_KEY];

    if (variable.type !== TEMPLATE_TOKEN_KIND || ids?.length !== 1 || Object.keys(variable.values).length !== 1) {
        return null;
    }

    return { aiChatReportTemplateId: ids[0], command: variable.labels[0] };
};

/**
 * Serialises a template to its token, the text the agent receives and the message stores.
 * @param template The template's id and command.
 * @returns The token string.
 * @throws {Error} When the id or command would not round-trip, e.g. an empty command.
 */
export const serialiseTemplateToken = (template: ITemplateMentionAttributes): string =>
    serialiseVariableToken(templateToVariableToken(template));

/**
 * What a chip shows: the command after its `/`.
 * @param command The template's command, without the `/`.
 * @returns The chip text, e.g. "/agent-performance".
 */
export const formatTemplateCommand = (command: string): string => `${TEMPLATE_COMMAND_PREFIX}${command}`;

/**
 * Removes every template body the agent attached to a user message, leaving the rest of the message as it was.
 * @param message A user message as stored, possibly holding `[template-body-start:<id>]…[template-body-end]` blocks.
 * @returns The message without them.
 */
export const stripTemplateBodies = (message: string): string => message.replace(TEMPLATE_BODY_REGEX, "");

/**
 * The ids of the templates a message uses, in the order they appear, each once. Template bodies are skipped, so a
 * token inside one never counts.
 * @param message A user message, possibly holding template tokens.
 * @returns The template ids.
 */
export const getTemplateIds = (message: string): string[] => {
    const ids = parseVariableTokens(stripTemplateBodies(message)).flatMap((segment) => {
        const template = segment.type === "variable" ? templateFromVariableToken(segment.variable) : null;

        return template === null ? [] : [template.aiChatReportTemplateId];
    });

    return [...new Set(ids)];
};

/**
 * Reads the template a `templateMention` node stands for.
 * @param node The `templateMention` node.
 * @returns Its id and command.
 */
export const templateFromNode = (node: ProseMirrorNode): ITemplateMentionAttributes => {
    const { aiChatReportTemplateId, command } = node.attrs;

    return { aiChatReportTemplateId, command };
};

/**
 * Whether a template can sit in a document: its token serialises, so it round-trips through the message text.
 * @param template The template's id and command.
 * @returns Whether a chip may hold it.
 */
export const isValidTemplate = (template: ITemplateMentionAttributes): boolean => {
    try {
        serialiseTemplateToken(template);

        return true;
    } catch {
        return false;
    }
};
